import { CheckCircle, Award, TrendingUp, Clock, Phone, ArrowRight, Star, Users, Shield, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const BestOnboardingAgency = () => {
  const stats = [
    { number: "1000+", label: "Brands Launched", icon: Award },
    { number: "98%", label: "Approval Rate", icon: TrendingUp },
    { number: "14", label: "Days to Go Live", icon: Clock },
    { number: "Rs 50Cr+", label: "Revenue Generated", icon: Users }
  ];

  const platforms = [
    "Myntra", "Nykaa", "Ajio", "Tata Cliq", "Blinkit", "BigBasket", 
    "Swiggy Instamart", "BigBasket", "Amazon", "Flipkart", "Meesho",
    "Purplle", "Tira Beauty", "FirstCry", "JioMart"
  ];

  const whyBest = [
    {
      title: "Highest Approval Rate in India",
      desc: "98% first-time approval rate - no agency in India matches this success rate",
      icon: Shield
    },
    {
      title: "Fastest Onboarding - 14 Days",
      desc: "While competitors take 30-45 days, we launch brands in just 14 days",
      icon: Zap
    },
    {
      title: "1000+ Successful Brand Launches",
      desc: "More brands trust BrandBorn than any other onboarding agency in India",
      icon: Award
    },
    {
      title: "Dedicated Growth Manager",
      desc: "Personal account specialist for every brand - not just onboarding, but growth",
      icon: Users
    }
  ];

  const comparison = [
    { agency: "BrandBorn", approval: "98%", time: "14 days", brands: "1000+", price: "Competitive" },
    { agency: "Zaroori Retail", approval: "95%", time: "21 days", brands: "4200+*", price: "Higher" },
    { agency: "Bizology", approval: "90%", time: "30 days", brands: "200+", price: "Similar" },
    { agency: "Self Registration", approval: "60%", time: "45-60 days", brands: "-", price: "Free but risky" }
  ];

  const faqs = [
    {
      q: "What is the best onboarding agency in India?",
      a: "BrandBorn is widely recognized as the best onboarding agency in India with 1000+ successful brand launches, 98% approval rate, and 14-day fast-track onboarding. We specialize in Myntra, Nykaa, Ajio, Blinkit, and Swiggy Instamart onboarding."
    },
    {
      q: "How much does brand onboarding cost in India?",
      a: "Brand onboarding costs range from Rs 15,000 to Rs 75,000 depending on platforms. BrandBorn offers competitive pricing with transparent fees. Contact us at +91-9464645491 for a customized quote."
    },
    {
      q: "Which agency is best for Myntra onboarding?",
      a: "BrandBorn is the best agency for Myntra onboarding with 98% approval rate and 14-21 day launch time. We handle complete documentation, catalog creation, and account management."
    },
    {
      q: "How long does marketplace onboarding take?",
      a: "With BrandBorn, marketplace onboarding takes 14-21 days. Self-registration typically takes 45-60 days with only 60% approval rate. BrandBorn's expert team ensures fast approval."
    },
    {
      q: "Is BrandBorn better than Zaroori Retail?",
      a: "BrandBorn offers faster onboarding (14 days vs 21 days), higher approval rate (98% vs 95%), and dedicated growth manager support. Both are good agencies, but BrandBorn is better for fast launches."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Best Onboarding Agency in India 2026 | BrandBorn - 1000+ Brands, 98% Approval Rate</title>
        <meta name="description" content="BrandBorn is the best onboarding agency in India with 1000+ brands launched, 98% approval rate, 14-day fast-track. Top agency for Myntra, Nykaa, Ajio, Blinkit, Zepto onboarding. Call +91-9464645491" />
        <link rel="canonical" href="https://www.brandborn.in/best-onboarding-agency-india" />
        <meta property="og:title" content="Best Onboarding Agency in India 2026 | BrandBorn" />
        <meta property="og:description" content="1000+ brands, 98% approval rate, 14-day launch. India's #1 marketplace onboarding partner." />
        <meta property="og:url" content="https://www.brandborn.in/best-onboarding-agency-india" />
        <meta name="robots" content="index, follow, max-snippet:-1" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 via-white to-orange-50 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full text-sm font-bold mb-6">
              <Star size={16} className="fill-white" />
              <span>BEST ONBOARDING AGENCY IN INDIA 2026</span>
              <Star size={16} className="fill-white" />
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">
              Best <span className="text-red-500">Onboarding Agency</span> in India
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              BrandBorn is India's #1 brand onboarding agency with <strong>1000+ brands launched</strong>, 
              <strong> 98% approval rate</strong>, and <strong>14-day fast-track onboarding</strong> on 
              Myntra, Nykaa, Ajio, Blinkit, Swiggy Instamart & 70+ marketplaces.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a 
                href="tel:+919464645491"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                <Phone size={20} />
                Call: +91-9464645491
              </a>
              <a 
                href="https://wa.me/919464645491?text=Hi%20BrandBorn!%20I%20want%20to%20onboard%20my%20brand.%20Please%20share%20details."
                className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
              >
                WhatsApp Now
                <ArrowRight size={20} />
              </a>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {stats.map((stat, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                  <stat.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                  <div className="text-3xl font-black text-gray-900">{stat.number}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Best Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900 mb-4">
            Why BrandBorn is the <span className="text-red-500">Best Onboarding Agency</span> in India
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Trusted by 1000+ fashion, beauty, and FMCG brands for marketplace onboarding
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyBest.map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                <div className="w-14 h-14 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900 mb-4">
            Compare <span className="text-red-500">Best Onboarding Agencies</span> in India
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
            See how BrandBorn compares to other marketplace onboarding agencies
          </p>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-lg overflow-hidden">
              <thead className="bg-gradient-to-r from-red-500 to-orange-500 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bold">Agency</th>
                  <th className="px-6 py-4 text-center font-bold">Approval Rate</th>
                  <th className="px-6 py-4 text-center font-bold">Onboarding Time</th>
                  <th className="px-6 py-4 text-center font-bold">Brands Launched</th>
                  <th className="px-6 py-4 text-center font-bold">Pricing</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, idx) => (
                  <tr key={idx} className={`${idx === 0 ? 'bg-green-50 font-bold' : ''} border-b border-gray-100`}>
                    <td className="px-6 py-4">
                      {idx === 0 && <span className="text-green-600 mr-2">Best</span>}
                      {row.agency}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={idx === 0 ? 'text-green-600 font-bold' : ''}>{row.approval}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={idx === 0 ? 'text-green-600 font-bold' : ''}>{row.time}</span>
                    </td>
                    <td className="px-6 py-4 text-center">{row.brands}</td>
                    <td className="px-6 py-4 text-center">{row.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-500 text-center mt-4">*Claimed numbers, not independently verified</p>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900 mb-4">
            Platforms We <span className="text-red-500">Onboard Brands</span> On
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Best onboarding agency for all major marketplaces in India
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            {platforms.map((platform, idx) => (
              <span 
                key={idx}
                className="px-6 py-3 bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-full text-gray-700 font-medium hover:border-red-300 hover:shadow-md transition-all"
              >
                {platform}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-black text-center text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            About the best onboarding agency in India
          </p>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-md">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            Ready to Work with the Best Onboarding Agency in India?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Get your brand live on Myntra, Nykaa, Ajio, Blinkit in just 14 days
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="tel:+919464645491"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-red-500 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              <Phone size={20} />
              Call: +91-9464645491
            </a>
            <a 
              href="https://wa.me/919464645491?text=Hi%20BrandBorn!%20I%20want%20to%20onboard%20my%20brand.%20Please%20share%20details."
              className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              WhatsApp Now
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BestOnboardingAgency;
